/**
 * Central wizard state store for Model & Persona Wizard
 * Manages environment, personas, model selections, and progress
 */

export type WizardEnvironment = {
  regulatorySensitivity: 'low' | 'medium' | 'high';
  latencyTolerance: 'snappy' | 'balanced' | 'batch';
  totalActiveUsersPerDay: number;
  concurrentUsers: number;
  turns: number;
  batch: number;
  precision: 4 | 8 | 16;
};

export type WizardPersona = {
  id: string;
  name: string;
  description: string;
  role: string;
  turnsPerSession: number;
  expertiseType: 'coding' | 'thinking' | 'long-conversation' | 'summarization' | 'general';
  inTok: number;
  outTok: number;
  color?: string;
};

export type WizardModelSelection = {
  selectedModelIds: string[];
  personaToModels: Record<string, string[]>;
  modelToPersonas: Record<string, string[]>;
};

export type WizardProgress = {
  environmentComplete: boolean;
  personasComplete: boolean;
  modelsComplete: boolean;
  sizingPreviewComplete: boolean;
  softwareComplete: boolean;
  currentStep: 1 | 2 | 3 | 4 | 5;
};

export type WizardState = {
  id: string | null;
  environment: WizardEnvironment;
  personas: WizardPersona[];
  modelSelections: WizardModelSelection;
  progress: WizardProgress;
};

type WizardStoreSubscriber = (state: WizardState) => void;

function createInitialState(): WizardState {
  return {
    id: null,
    environment: {
      regulatorySensitivity: 'medium',
      latencyTolerance: 'balanced',
      totalActiveUsersPerDay: 1000,
      concurrentUsers: 100, // Fixed: 10% concurrency ratio
      turns: 3,
      batch: 256,
      precision: 8,
    },
    personas: [],
    modelSelections: {
      selectedModelIds: [],
      personaToModels: {},
      modelToPersonas: {},
    },
    progress: {
      environmentComplete: false,
      personasComplete: false,
      modelsComplete: false,
      sizingPreviewComplete: false,
      softwareComplete: false,
      currentStep: 1,
    },
  };
}

let state: WizardState = createInitialState();
const subscribers = new Set<WizardStoreSubscriber>();

function notify() {
  subscribers.forEach((subscriber) => subscriber(state));
}

export const wizardStore = {
  subscribe(subscriber: WizardStoreSubscriber) {
    subscribers.add(subscriber);
    subscriber(state);
    return () => {
      subscribers.delete(subscriber);
    };
  },

  getState(): WizardState {
    return { ...state };
  },

  setState(patch: Partial<WizardState>) {
    state = { ...state, ...patch };
    notify();
  },

  updateEnvironment(env: Partial<WizardEnvironment>) {
    state = {
      ...state,
      environment: { ...state.environment, ...env }
    };
    notify();
  },

  setPersonas(personas: WizardPersona[]) {
    console.log('wizardStore.setPersonas called with:', personas.length, 'personas', personas);
    state = { ...state, personas: [...personas] };
    console.log('wizardStore state after setPersonas:', state.personas.length, state.personas);
    notify();
  },

  addPersona(persona: WizardPersona) {
    console.log('wizardStore.addPersona called with:', persona);
    state = { ...state, personas: [...state.personas, persona] };
    console.log('wizardStore state after addPersona:', state.personas.length, state.personas);
    notify();
  },

  updatePersona(id: string, updates: Partial<WizardPersona>) {
    state = {
      ...state,
      personas: state.personas.map((p) =>
        p.id === id ? { ...p, ...updates } : p
      )
    };
    notify();
  },

  removePersona(id: string) {
    state = {
      ...state,
      personas: state.personas.filter((p) => p.id !== id)
    };
    // Clean up model mappings
    Object.keys(state.modelSelections.personaToModels).forEach((pid) => {
      if (pid === id) {
        delete state.modelSelections.personaToModels[pid];
      }
    });
    Object.keys(state.modelSelections.modelToPersonas).forEach((mid) => {
      state.modelSelections.modelToPersonas[mid] = 
        state.modelSelections.modelToPersonas[mid].filter((pid) => pid !== id);
    });
    notify();
  },

  updateModelSelections(selections: Partial<WizardModelSelection>) {
    state.modelSelections = { ...state.modelSelections, ...selections };
    notify();
  },

  toggleModelSelection(modelId: string) {
    const isSelected = state.modelSelections.selectedModelIds.includes(modelId);
    
    if (isSelected) {
      // Create new objects for complete immutability
      const newModelToPersonas = { ...state.modelSelections.modelToPersonas };
      delete newModelToPersonas[modelId];
      
      const newPersonaToModels: Record<string, string[]> = {};
      Object.keys(state.modelSelections.personaToModels).forEach((pid) => {
        newPersonaToModels[pid] = state.modelSelections.personaToModels[pid].filter(
          (mid) => mid !== modelId
        );
      });
      
      state = {
        ...state,
        modelSelections: {
          ...state.modelSelections,
          selectedModelIds: state.modelSelections.selectedModelIds.filter(
            (id) => id !== modelId
          ),
          modelToPersonas: newModelToPersonas,
          personaToModels: newPersonaToModels,
        }
      };
    } else {
      state = {
        ...state,
        modelSelections: {
          ...state.modelSelections,
          selectedModelIds: [
            ...state.modelSelections.selectedModelIds,
            modelId,
          ],
        }
      };
    }
    notify();
  },

  attachModelToPersonas(modelId: string, personaIds: string[]) {
    // Update modelToPersonas
    state.modelSelections.modelToPersonas[modelId] = personaIds;
    
    // Update personaToModels
    personaIds.forEach((pid) => {
      const current = state.modelSelections.personaToModels[pid] || [];
      if (!current.includes(modelId)) {
        state.modelSelections.personaToModels[pid] = [...current, modelId];
      }
    });
    
    notify();
  },

  updateProgress(progress: Partial<WizardProgress>) {
    state.progress = { ...state.progress, ...progress };
    notify();
  },

  reset() {
    state = createInitialState();
    notify();
  },

  loadState(loadedState: WizardState) {
    state = loadedState;
    notify();
  },
};
